import { getQueryParamCaseInsensitive, tryParseGuid } from '@ngsos/portal-core/incidents';

import type { IncidentArchiveFilters } from '@/features/incident-archive/filters';
import {
  applyFiltersToSearch,
  defaultIncidentArchiveFilters,
  parseDotNetDate,
  sanitizeFilters,
} from '@/features/incident-archive/filters';
import { incidentArchiveRoute, incidentDetailRoute } from '@/features/shared/routes';

export type OpenIncidentTarget =
  | { kind: 'redirect'; url: string }
  | { kind: 'search'; filters: IncidentArchiveFilters }
  | { kind: 'notFound' };

export type OpenIncidentMatch = { incidentId: string };

export function resolveOpenIncidentTarget(
  searchParams: URLSearchParams,
  now: Date = new Date(),
): OpenIncidentTarget {
  const id = readParameter(searchParams, 'id');
  if (id !== null) {
    const incidentId = tryParseGuid(id);
    return incidentId === null
      ? { kind: 'notFound' }
      : { kind: 'redirect', url: incidentDetailRoute(incidentId) };
  }

  const phoneNumber = readParameter(searchParams, 'phoneNumber');
  const date = readParameter(searchParams, 'date');
  if (phoneNumber === null && date === null) {
    return { kind: 'redirect', url: incidentArchiveRoute() };
  }

  return { kind: 'search', filters: buildSearchFilters(phoneNumber, date, now) };
}

export function incidentArchiveUrl(filters: IncidentArchiveFilters): string {
  return `${incidentArchiveRoute()}${applyFiltersToSearch('', filters)}`;
}

export function openIncidentSearchUrl(
  matches: readonly OpenIncidentMatch[],
  filters: IncidentArchiveFilters,
): string {
  const [onlyMatch] = matches;

  return matches.length === 1 && onlyMatch !== undefined
    ? incidentDetailRoute(onlyMatch.incidentId)
    : incidentArchiveUrl(filters);
}

function readParameter(searchParams: URLSearchParams, name: string): string | null {
  const value = getQueryParamCaseInsensitive(searchParams, name);
  return value === null || value.trim() === '' ? null : value;
}

function buildSearchFilters(
  phoneNumber: string | null,
  date: string | null,
  now: Date,
): IncidentArchiveFilters {
  const defaults = defaultIncidentArchiveFilters(now);
  const day = date === null ? null : parseDotNetDate(date);

  return sanitizeFilters({ ...defaults, from: day ?? defaults.from, to: day ?? defaults.to, phoneNumber });
}
